/* eslint-disable @typescript-eslint/strict-boolean-expressions */
import { createAppStore } from '@store/createAppStore';
import { ILoadBoardViewModel } from '@store/loadboard/types';
import { CarrierAutoCompleteModel } from '@typeDefinitions/carrierTypes';
import { carrierDefaultsT } from '@typeDefinitions/capacityTypes';

import { Carrier } from '..';
import {
  CachedCarrierT,
  CapacitySidepanelModelT,
  CapacityStoreT,
  DrawerStoreT,
  EquipmentT,
  OfferT,
  TruckTransportT,
  defaultBid,
  initCarrier,
  initTruck,
  initialCarrierDefaults,
} from './types';

export const [defaultStore, setDefaultStore] = createAppStore<carrierDefaultsT>(
  {
    ...initialCarrierDefaults,
  },
  'defaultStore',
);
export const [truckStore, setTruckStore] = createAppStore<TruckTransportT>(
  initTruck,
  'truckStore',
);
export const [carrierCapStore, setCarrierCapStore] = createAppStore<Carrier>(
  initCarrier,
  'carrierCapStore',
);
export const [drawerStore, setDrawerStore] = createAppStore<DrawerStoreT>(
  defaultBid,
  'drawerStore',
);
export const [capacityStore, setCapacityStore] = createAppStore<CapacityStoreT>(
  {
    carriers: {
      owned: [],
      developing: [],
      recent: [],
      isCapacityTeamEnabled: false,
    },
    selectedLoads: {},
    equipment: [],
    loads: [],
    isLoading: false,
    selectedTruckId: undefined,
    offers: [],
    offerAmounts: {},
  },
  'capacityStore',
);

export const setCarriers = (carriers: CapacitySidepanelModelT) => {
  setCapacityStore('carriers', carriers);
};

export const addRecentCarrier = (
  carrier: CarrierAutoCompleteModel | Carrier,
) => {
  setCapacityStore('carriers', (carriers: CapacitySidepanelModelT) => {
    const newRecentCarrier: CachedCarrierT = {
      carrierId: carrier.id,
      carrierName: carrier.name,
      noCapacityForTheDay: false,
      hasBeenCalledForTheDay: false,
    };

    return {
      ...carriers,
      recent: [...carriers.recent, newRecentCarrier],
    } as CapacitySidepanelModelT;
  });
};

export const toggleSelectedLoad = (loadId: number) => {
  setCapacityStore('selectedLoads', (selectedLoads) => {
    const newSelectedLoads = { ...selectedLoads };

    newSelectedLoads[loadId] = !Boolean(newSelectedLoads[loadId]);

    return newSelectedLoads;
  });
};

export const setSelectedLoads = (loads: number[]) => {
  if (loads.length === 0) {
    setCapacityStore('selectedLoads', (prev) => {
      const newSelectedLoads = { ...prev };

      for (const k in newSelectedLoads) {
        newSelectedLoads[k] = false;
      }

      return newSelectedLoads;
    });

    return;
  }

  setCapacityStore('selectedLoads', () => {
    const newSelectedLoads: Record<number, boolean> = {};

    loads.forEach((loadId) => {
      newSelectedLoads[loadId] = !Boolean(newSelectedLoads[loadId]);
    });

    return newSelectedLoads;
  });
};

export const emptySelectedLoads = () => {
  setCapacityStore('selectedLoads', {});
  setCapacityStore('loads', []);
};

export const setLoads = (loads: ILoadBoardViewModel[]) => {
  setCapacityStore('loads', loads);
};

export const removeLoadById = (loadId: number) => {
  setCapacityStore('loads', (loads) => {
    return [...loads].filter((l) => l.loadId !== loadId);
  });
};

export const setEquipment = (equipment: EquipmentT[]) => {
  setCapacityStore('equipment', equipment);
};

export const setOffers = (offers: OfferT[]) => {
  setCapacityStore('offers', offers);
  setCapacityStore('offerAmounts', () => {
    return offers.reduce(
      (acc, offer) => {
        acc[offer.id] = offer.amount;

        return acc;
      },
      {} as Record<number, number>,
    );
  });
};

export const deleteOfferById = (id: number) => {
  const idx = capacityStore.offers.findIndex((o) => o.id === id);

  if (idx >= 0) {
    const offers = [...capacityStore.offers];
    offers.splice(idx, 1);

    setOffers(offers);
  }
};

export const updateOffer = (id: number, offer: Partial<OfferT>) => {
  setCapacityStore('offers', (offers) => {
    // eslint-disable-next-line @typescript-eslint/no-unnecessary-type-assertion
    const idx = (offers as OfferT[]).findIndex((v) => v.id === id);

    if (idx < 0) {
      return offers;
    }

    const newOffers = [...offers];
    newOffers[idx] = {
      ...newOffers[idx],
      ...offer,
    };

    return newOffers;
  });

  if ('amount' in offer && !!offer.amount && !isNaN(offer.amount)) {
    setCapacityStore('offerAmounts', id, offer.amount);
  }
};

export const setOfferAmount = (id: number, amount: number) => {
  setCapacityStore('offerAmounts', id, amount);
};
